import React, { useState, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import Button from 'react-bootstrap/Button';
import { FormattedMessage } from 'react-intl';
import { Spinner } from 'react-bootstrap';
import PropdocsModal from '../../../../../modal/PropdocsModal';
import { TitleFormattedMessage } from '../../../../../titleFormattedMessage';
import { useNotifications } from '../../../../../../notifications';
import { getDocumentById } from '../../../../../../api/queries_v2';

const REMIND_INTERNAL_REVIEWER = gql`
  mutation RemindInternalReviewer(
    $documentId: ID!
    $approvalRequestId: ID!
    $additionalMessage: String
  ) {
    remindInternalReviewer(
      documentId: $documentId
      approvalRequestId: $approvalRequestId
      additionalMessage: $additionalMessage
    )
  }
`;

interface Props {
  show: boolean;
  onHide: () => void;
  documentId: string;
  approvalRequestId: string;
  reviewers?: Array<{ id: string; fullName: string; email: string }>;
  useApprovalOrder?: boolean;
  currentReviewer?: { fullName: string; email: string };
}

const SendInternalReminderModal = ({
  show,
  onHide,
  documentId,
  approvalRequestId,
  reviewers = [],
  useApprovalOrder = false,
  currentReviewer,
}: Props) => {
  const [isSending, setIsSending] = useState(false);
  const messageRef = useRef<HTMLTextAreaElement>(null);
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();

  const [remindReviewer] = useMutation(REMIND_INTERNAL_REVIEWER, {
    refetchQueries: [
      {
        query: getDocumentById,
        variables: { documentId },
      },
    ],
  });

  const handleSendReminder = async () => {
    setIsSending(true);
    const additionalMessage = messageRef.current?.value;

    try {
      await remindReviewer({
        variables: {
          documentId,
          approvalRequestId,
          additionalMessage,
        },
      });

      notifySuccess({
        message: {
          id: 'internal.reminder.success',
          defaultMessage: 'Reminder sent successfully',
        },
      });
      onHide();
    } catch (error: any) {
      notifyError({
        message: {
          id: 'internal.reminder.error',
          defaultMessage: error.message || 'Failed to send reminder',
        },
      });
    } finally {
      setIsSending(false);
    }
  };

  const getReviewersList = () => {
    if (useApprovalOrder && currentReviewer) {
      return `Current Reviewer: ${currentReviewer.fullName} (${currentReviewer.email})`;
    }

    if (!useApprovalOrder && reviewers.length > 0) {
      const pendingReviewers = reviewers
        .map(r => `${r.fullName} (${r.email})`)
        .join(', ');
      return `Reviewers: ${pendingReviewers}`;
    }

    return 'No pending reviewers';
  };

  return (
    <PropdocsModal
      size="lg"
      show={show}
      setShow={onHide}
      title={
        <FormattedMessage
          id="internal-reminder-modal.title"
          defaultMessage="Send Reminder to Internal Reviewers"
        />
      }
    >
      <>
        <div style={{ marginBottom: '1.5rem' }}>
          <FormattedMessage
            id="internal-reminder-modal.message"
            defaultMessage="Would you like to send a reminder to the internal reviewer(s) to complete their review?"
          />
        </div>

        <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
          <strong>{getReviewersList()}</strong>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label htmlFor="optional-message" style={{ display: 'block', marginBottom: '0.5rem' }}>
            <FormattedMessage
              id="internal-reminder-modal.optional-message"
              defaultMessage="Optional Message"
            />
          </label>
          <textarea
            ref={messageRef}
            id="optional-message"
            className="form-control"
            rows={4}
            placeholder="Add an optional message to include with the reminder..."
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1rem' }}>
          <Button variant="outline-primary" onClick={onHide} disabled={isSending}>
            <TitleFormattedMessage
              id="internal-reminder-modal.cancel"
              defaultMessage="Cancel"
            />
          </Button>
          <Button variant="primary" onClick={handleSendReminder} disabled={isSending}>
            {isSending && <Spinner animation="border" size="sm" style={{ marginRight: '0.5rem' }} />}
            <TitleFormattedMessage
              id="internal-reminder-modal.send"
              defaultMessage="Send Reminder"
            />
          </Button>
        </div>
      </>
    </PropdocsModal>
  );
};

export default SendInternalReminderModal;